import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Globe, ExternalLink, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { useLanguage } from "@/hooks/useLanguage";
import gepositLogo from "@/assets/geposit-logo.png";

const languages = [
  { code: "sv", label: "SV", name: "Svenska" },
  { code: "no", label: "NO", name: "Norsk" },
  { code: "fi", label: "FI", name: "Suomi" },
  { code: "da", label: "DA", name: "Dansk" },
  { code: "en", label: "EN", name: "English" },
];

const infoSubLinks = [
  { key: "nav.nyheter", href: "/information/nyheter" },
  { key: "nav.villkor", href: "/information/villkor" },
  { key: "nav.omGeposit", href: "/om-geposit" },
  { key: "nav.developer", href: "/developer" },
];

export function Header() {
  const { t } = useTranslation();
  const { lang, getLocalizedPath, changeLanguage: changeLang } = useLanguage();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileInfoOpen, setIsMobileInfoOpen] = useState(false);
  const [infoHoverOpen, setInfoHoverOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileInfoOpen(false);
  }, [location.pathname]);

  // Helper to check if a path is active (strip lang prefix for comparison)
  const pathWithoutLang = location.pathname.replace(/^\/(sv|en|no|da|fi)/, "") || "/";

  const isInfoActive = infoSubLinks.some((l) => pathWithoutLang.startsWith(l.href)) || pathWithoutLang === "/developer";

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isScrolled
          ? "bg-background/95 backdrop-blur-md shadow-sm border-b border-border"
          : "bg-transparent"
      )}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to={getLocalizedPath("/")} className="flex items-center">
            <img src={gepositLogo} alt="Geposit" width={243} height={40} className="h-8 lg:h-10 w-auto" style={{ aspectRatio: '481/80' }} />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" aria-label={t("nav.start")}>
            {/* Lösningar */}
            <Link
              to={getLocalizedPath("/losningar")}
              className={cn(
                "text-sm font-medium transition-colors hover:text-primary",
                pathWithoutLang.startsWith("/losningar") ? "text-primary" : "text-foreground/70"
              )}
            >
              {t("nav.losningar")}
            </Link>

            {/* Tjänster */}
            <Link
              to={getLocalizedPath("/tjanster")}
              className={cn(
                "text-sm font-medium transition-colors hover:text-primary",
                pathWithoutLang.startsWith("/tjanster") ? "text-primary" : "text-foreground/70"
              )}
            >
              {t("nav.produkter")}
            </Link>

            {/* Information hover dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setInfoHoverOpen(true)}
              onMouseLeave={() => setInfoHoverOpen(false)}
              onFocus={() => setInfoHoverOpen(true)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setInfoHoverOpen(false);
                }
              }}
            >
              <button
                className={cn(
                  "text-sm font-medium transition-colors hover:text-primary inline-flex items-center gap-1",
                  isInfoActive ? "text-primary" : "text-foreground/70"
                )}
                aria-expanded={infoHoverOpen}
                aria-haspopup="true"
              >
                {t("nav.information")}
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", infoHoverOpen && "rotate-180")} aria-hidden="true" />
              </button>
              {infoHoverOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
                  <div className="bg-popover border border-border rounded-lg shadow-lg min-w-[160px] py-1">
                    {infoSubLinks.map((link) => (
                      <Link
                        key={link.key}
                        to={getLocalizedPath(link.href)}
                        className={cn(
                          "block px-4 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                          pathWithoutLang === link.href ? "font-semibold text-primary" : "text-foreground"
                        )}
                      >
                        {t(link.key)}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Kontakt */}
            <Link
              to={getLocalizedPath("/kontakt")}
              className={cn(
                "text-sm font-medium transition-colors hover:text-primary",
                pathWithoutLang === "/kontakt" ? "text-primary" : "text-foreground/70"
              )}
            >
              {t("nav.kontakt")}
            </Link>

            {/* Beställ */}
            <Link
              to={getLocalizedPath("/bestall")}
              className={cn(
                "text-sm font-semibold transition-colors px-3 py-1.5 rounded-md",
                pathWithoutLang === "/bestall"
                   ? "bg-coral-interactive text-coral-foreground"
                   : "bg-coral-interactive/10 text-coral-interactive hover:bg-coral-interactive/20"
              )}
            >
              {t("nav.bestall")}
            </Link>
          </nav>

          {/* Desktop Right Side */}
          <div className="hidden lg:flex items-center gap-3">
            <ThemeToggle />
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 text-foreground/70">
                  <Globe className="h-4 w-4" />
                  <span className="text-xs font-semibold">
                    {languages.find((l) => l.code === lang)?.label || "SV"}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    onClick={() => changeLang(l.code)}
                    className={cn(
                      "cursor-pointer",
                      lang === l.code && "font-semibold text-primary"
                    )}
                  >
                    <span className="font-mono text-xs mr-2">{l.label}</span>
                    {l.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Login Link */}
            <Button variant="ghost" size="sm" asChild className="gap-1.5 text-foreground/70 font-medium">
              <a href="https://app.geposit.se" target="_blank" rel="noopener noreferrer">
                {t("nav.loggain")}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>

            {/* CTA Button */}
            <Button asChild variant="ghost" className="bg-coral-interactive text-coral-foreground hover:bg-coral-interactive/90 hover:text-coral-foreground font-semibold shadow-md h-10 px-4 py-2">
              <Link to={getLocalizedPath("/kontakt?type=demo")}>{t("nav.bokademo")}</Link>
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 text-foreground"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Stäng meny" : "Öppna meny"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden pb-6 border-t border-border bg-background max-h-[calc(100dvh-4rem)] overflow-y-auto">
            <nav className="flex flex-col gap-1 pt-4" aria-label="Mobilmeny">
              {/* Lösningar */}
              <Link
                to={getLocalizedPath("/losningar")}
                className={cn(
                  "px-3 py-2.5 rounded-md text-base font-medium transition-colors",
                  pathWithoutLang.startsWith("/losningar")
                    ? "text-primary bg-petrol-light"
                    : "text-foreground/70 hover:text-primary hover:bg-muted"
                )}
              >
                {t("nav.losningar")}
              </Link>

              {/* Tjänster */}
              <Link
                to={getLocalizedPath("/tjanster")}
                className={cn(
                  "px-3 py-2.5 rounded-md text-base font-medium transition-colors",
                  pathWithoutLang.startsWith("/tjanster")
                    ? "text-primary bg-petrol-light"
                    : "text-foreground/70 hover:text-primary hover:bg-muted"
                )}
              >
                {t("nav.produkter")}
              </Link>

              {/* Information — expandable */}
              <button
                onClick={() => setIsMobileInfoOpen(!isMobileInfoOpen)}
                className={cn(
                  "px-3 py-2.5 rounded-md text-base font-medium transition-colors text-left flex items-center justify-between",
                  isInfoActive
                    ? "text-primary bg-petrol-light"
                    : "text-foreground/70 hover:text-primary hover:bg-muted"
                )}
              >
                {t("nav.information")}
                <ChevronDown className={cn("h-4 w-4 transition-transform", isMobileInfoOpen && "rotate-180")} />
              </button>
              {isMobileInfoOpen && (
                <div className="flex flex-col gap-1 pl-4">
                  {infoSubLinks.map((link) => (
                    <Link
                      key={link.key}
                      to={getLocalizedPath(link.href)}
                      className={cn(
                        "px-3 py-2 rounded-md text-sm font-medium transition-colors",
                        pathWithoutLang === link.href
                          ? "text-primary bg-petrol-light"
                          : "text-foreground/70 hover:text-primary hover:bg-muted"
                      )}
                    >
                      {t(link.key)}
                    </Link>
                  ))}
                </div>
              )}

              {/* Kontakt */}
              <Link
                to={getLocalizedPath("/kontakt")}
                className={cn(
                  "px-3 py-2.5 rounded-md text-base font-medium transition-colors",
                  pathWithoutLang === "/kontakt"
                    ? "text-primary bg-petrol-light"
                    : "text-foreground/70 hover:text-primary hover:bg-muted"
                )}
              >
              {t("nav.kontakt")}
              </Link>

              {/* Beställ */}
              <Link
                to={getLocalizedPath("/bestall")}
                className={cn(
                  "px-3 py-2.5 rounded-md text-base font-semibold transition-colors",
                  pathWithoutLang === "/bestall"
                    ? "bg-coral-interactive text-coral-foreground"
                    : "text-coral-interactive hover:bg-coral-interactive/10"
                )}
              >
                {t("nav.bestall")}
              </Link>
            </nav>

            <div className="mt-4 pt-4 border-t border-border space-y-3 px-3">
              {/* Theme + Language options */}
              <div className="flex items-center gap-1">
                <ThemeToggle />
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => changeLang(l.code)}
                    className={cn(
                      "min-w-[44px] min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-md transition-colors",
                      lang === l.code
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    )}
                  >
                    {l.label}
                  </button>
                ))}
              </div>

              <Button variant="outline" asChild className="w-full gap-2 font-medium">
                <a href="https://app.geposit.se" target="_blank" rel="noopener noreferrer">
                  {t("nav.loggain")}
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>

              <Button asChild className="bg-coral-interactive text-coral-foreground hover:bg-coral-interactive/90 font-semibold w-full">
                <Link to={getLocalizedPath("/kontakt?type=demo")}>{t("nav.bokademo")}</Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
